import { Link } from "react-router-dom";
import { Section, FinalCTA, SecondaryCTA } from "@/components/v2/blocks";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Plug, Gauge, Receipt, Wallet, Zap, Flame, Scale, Repeat, FileUp, ArrowRight } from "lucide-react";

const CONTENU = [
  { icon: FileText, title: "Coordonnées du contrat", text: "Vos identifiants, l'adresse et la durée du contrat." },
  { icon: Plug, title: "Abonnement", text: "La part fixe, indépendante de votre consommation." },
  { icon: Gauge, title: "Consommation", text: "Les kWh consommés sur la période facturée." },
  { icon: Receipt, title: "Taxes et contributions", text: "Les taxes et contributions réglementées." },
  { icon: Wallet, title: "Montant total", text: "Le montant à régler, détaillé poste par poste." },
];

const REFERENCES = [
  { icon: Zap, title: "PDL / PRM", text: "Le Point de Livraison (ou Point de Référence Mesure), pour l'électricité." },
  { icon: Flame, title: "PCE", text: "Le Point de Comptage et d'Estimation, pour le gaz." },
];

const PLUS_LOIN = [
  { to: "/particuliers/comparer-les-offres", label: "Comparer les offres", sub: "Particuliers", icon: Scale },
  { to: "/particuliers/changer-de-fournisseur", label: "Changer de fournisseur", sub: "Particuliers", icon: Repeat },
  { to: "/contact?type=facture", label: "Déposer ma facture", sub: "Analyse gratuite", icon: FileUp },
];

function RevealCard({ icon: Icon, title, text }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 ease-out hover:-translate-y-3 hover:scale-[1.05] hover:border-primary hover:bg-primary hover:shadow-2xl hover:shadow-primary/40">
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(130% 90% at 50% 0%, rgba(196,240,66,0.30), transparent 62%)" }}
      />
      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-lg group-hover:shadow-accent/40">
          <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
        </div>
        <h3 className="font-display text-base font-bold text-primary mt-5 leading-snug transition-colors duration-500 group-hover:text-white">{title}</h3>
        <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-2 group-hover:grid-rows-[1fr] group-hover:opacity-100">
          <p className="overflow-hidden text-sm leading-relaxed text-white/85">{text}</p>
        </div>
      </div>
    </div>
  );
}

export default function ComprendreFacture() {
  return (
    <div>
      <PageHeroPhoto
        cmsKey="comprendre"
        eyebrow="Particuliers · Comprendre sa facture"
        title="Comprendre sa facture d'énergie simplement"
        subtitle="Une facture d'électricité ou de gaz contient de nombreuses informations utiles. Energy Market vous aide à les interpréter afin de mieux comprendre votre situation énergétique."
        image="/img/comprendre-facture.png"
        overlay="cinematic"
        flipLogo
        wideCard
        heroAside={
          <div className="flex justify-end -mr-2 lg:-mr-8">
            <div className="relative">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 360" role="img" aria-label="Facture d'énergie" className="relative h-56 w-auto md:h-72">
                {/* feuille avec coin corné */}
                <path
                  d="M32 40 L184 40 L222 78 L222 306 Q222 320 208 320 L32 320 Q18 320 18 306 L18 54 Q18 40 32 40 Z"
                  fill="rgba(255,255,255,0.18)"
                  stroke="#5C7A2E"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M184 40 L184 70 Q184 78 192 78 L222 78 Z"
                  fill="rgba(255,255,255,0.28)"
                  stroke="#5C7A2E"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                {/* en-tête : pastille € + intitulés */}
                <circle cx="54" cy="86" r="15" fill="rgba(196,240,66,0.30)" stroke="#5C7A2E" strokeWidth="1.8" />
                <text x="54" y="87" textAnchor="middle" dominantBaseline="central" fill="#C7FF2F" fontSize="18" fontWeight="700" fontFamily="inherit">€</text>
                <path d="M80 80 h74" stroke="rgba(255,255,255,0.55)" strokeWidth="7" strokeLinecap="round" />
                <path d="M80 96 h48" stroke="rgba(255,255,255,0.25)" strokeWidth="5" strokeLinecap="round" />
                <path d="M34 122 h172" stroke="rgba(92,122,46,0.35)" strokeWidth="1" strokeLinecap="round" />
                {/* postes de la facture (au repos) */}
                <g>
                  <rect x="32" y="138" width="176" height="30" rx="9" fill="rgba(255,255,255,0.06)" />
                  <path d="M44 153 h64" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 153 h26" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                  <rect x="32" y="178" width="176" height="30" rx="9" fill="rgba(255,255,255,0.06)" />
                  <path d="M44 193 h52" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 193 h26" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                  <rect x="32" y="218" width="176" height="30" rx="9" fill="rgba(255,255,255,0.06)" />
                  <path d="M44 233 h58" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 233 h26" stroke="rgba(255,255,255,0.45)" strokeWidth="6" strokeLinecap="round" />
                </g>
                {/* postes qui s'allument l'un après l'autre */}
                <g className="em-step em-step--1">
                  <rect x="32" y="138" width="176" height="30" rx="9" fill="rgba(196,240,66,0.22)" stroke="rgba(196,240,66,0.45)" strokeWidth="1" />
                  <path d="M44 153 h64" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 153 h26" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                </g>
                <g className="em-step em-step--2">
                  <rect x="32" y="178" width="176" height="30" rx="9" fill="rgba(196,240,66,0.22)" stroke="rgba(196,240,66,0.45)" strokeWidth="1" />
                  <path d="M44 193 h52" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 193 h26" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                </g>
                <g className="em-step em-step--3">
                  <rect x="32" y="218" width="176" height="30" rx="9" fill="rgba(196,240,66,0.22)" stroke="rgba(196,240,66,0.45)" strokeWidth="1" />
                  <path d="M44 233 h58" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                  <path d="M170 233 h26" stroke="#C4F042" strokeWidth="6" strokeLinecap="round" />
                </g>
                {/* total */}
                <path d="M34 266 h172" stroke="rgba(92,122,46,0.35)" strokeWidth="1" strokeLinecap="round" />
                <path d="M44 292 h52" stroke="rgba(255,255,255,0.60)" strokeWidth="8" strokeLinecap="round" />
                <rect x="152" y="278" width="54" height="28" rx="9" fill="rgba(196,240,66,0.30)" stroke="#5C7A2E" strokeWidth="1.8" />
                <text x="179" y="293" textAnchor="middle" dominantBaseline="central" fill="#C7FF2F" fontSize="15" fontWeight="700" fontFamily="inherit">€</text>
              </svg>
            </div>
          </div>
        }
        highlightsLabel="Ce que contient votre facture"
        highlights={[
          "Les coordonnées et la durée du contrat",
          "L'abonnement et la consommation",
          "Les taxes et contributions",
          "Les références : PDL, PRM, PCE",
        ]}
      />

      {/* CONTENU DE LA FACTURE — cartes à révélation */}
      <Section cmsKey="comprendre.contenu" eyebrow="Les principales informations" title="Ce que contient votre facture" intro="Cinq éléments clés à repérer pour mieux comprendre ce que vous payez.">
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {CONTENU.map((c) => <RevealCard key={c.title} {...c} />)}
        </div>
      </Section>

      {/* RÉFÉRENCES — cartes de définition */}
      <Section cmsKey="comprendre.references" eyebrow="Les références importantes" title="PDL, PRM, PCE : de quoi parle-t-on ?" intro="Ces références techniques sont essentielles dans de nombreuses démarches liées à l'énergie." muted>
        <div className="grid gap-5 sm:grid-cols-2">
          {REFERENCES.map(({ icon: Icon, title, text }) => (
            <Card key={title} className="rounded-2xl border-border bg-card em-card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-primary">{title}</h3>
                  <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* DÉPÔT FACTURE */}
      <Section cmsKey="comprendre.depot" eyebrow="Déposez votre facture" title="Pourquoi nous transmettre votre facture ?" intro="Votre facture contient déjà une grande partie des informations nécessaires à une première analyse : identifier le fournisseur, comprendre certaines caractéristiques du contrat et disposer des références techniques utiles.">
        <SecondaryCTA />
      </Section>

      {/* MAILLAGE */}
      <Section title="Pour aller plus loin" muted>
        <div className="grid gap-4 sm:grid-cols-3">
          {PLUS_LOIN.map(({ to, label, sub, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </Section>

      <FinalCTA title="Déposez votre facture pour une première analyse" />
    </div>
  );
}
